import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { getFirestore, collection, addDoc, query, where, getDocs, orderBy } from 'firebase/firestore';
import { auth } from '../firebase';

const db = getFirestore();

const SocialMedia = ({ setCurrentPage }) => {
  const [url, setUrl] = useState('');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [storedPosts, setStoredPosts] = useState([]);
  const [expandedPost, setExpandedPost] = useState(null);

  useEffect(() => {
    fetchStoredPosts();
  }, []);

  const fetchStoredPosts = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const q = query(
        collection(db, "SocialMediaPosts"),
        where("userId", "==", user.uid),
        orderBy("timestamp", "desc")
      );

      const querySnapshot = await getDocs(q);
      const posts = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStoredPosts(posts);
    } catch (error) {
      console.error("Error fetching stored posts:", error);
      setError(`Failed to fetch stored posts: ${error.message}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setResult(null);

    const webhookUrl = 'https://hook.eu2.make.com/f1c7os3jccy3nedp5k5c1eartat8qfxc';

    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Custom-Header': 'SocialMedia',
        },
        body: JSON.stringify({ url: url }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Data:', data);
      const processedData = {
        twitterPost: data.twitterPost,
        facebookPost: data.facebookPost,
        linkedinPost: data.linkedinPost,
      };
      console.log('Processed data:', processedData);
      setResult(processedData);

      try {
        const user = auth.currentUser;
        if (!user) {
          throw new Error('User not authenticated!');
        }

        const docRef = await addDoc(collection(db, "SocialMediaPosts"), {
          userId: user.uid,
          url: url,
          ...processedData,
          timestamp: new Date()
        });
        console.log("Document written with ID: ", docRef.id);
        fetchStoredPosts(); // Refresh the list after adding a new post
      } catch (firestoreError) {
        console.error("Error adding document to Firestore: ", firestoreError);
        setError(`Failed to store result in database: ${firestoreError.message}`);
      }

    } catch (e) {
      console.error("There was a problem with the fetch operation:", e);
      setError(`An error occurred: ${e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePost = (id) => {
    setExpandedPost(expandedPost === id ? null : id);
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <button onClick={() => setCurrentPage('home')} className="mb-4 flex items-center text-[rgb(100,204,201)] hover:underline">
        <ArrowLeft className="mr-2" /> Back to Dashboard
      </button>
      <h2 className="text-2xl font-bold mb-4">Social Media Post Generator</h2>
      <form onSubmit={handleSubmit} className="mb-6">
        <div className="mb-4">
          <label htmlFor="url" className="block text-sm font-medium text-gray-700">Article URL</label>
          <input
            type="url"
            id="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(100,204,201)] focus:ring focus:ring-[rgb(100,204,201)] focus:ring-opacity-50"
            required
          />
        </div>
        <button 
          type="submit" 
          className="bg-[rgb(100,204,201)] text-white py-2 px-4 rounded hover:bg-[rgb(90,184,181)] transition-colors"
          disabled={isLoading}
        >
          {isLoading ? 'Generating...' : 'Generate Posts'}
        </button>
      </form>
      {isLoading && (
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[rgb(100,204,201)]"></div>
        </div>
      )}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      )}
      {result && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h3 className="text-xl font-semibold mb-2">Latest Generated Posts</h3>
          <div className="mb-4 twitter-preview">
            <h4 className="font-medium">Twitter:</h4>
            <p>{result.twitterPost}</p>
          </div>
          <div className="mb-4 facebook-preview">
            <h4 className="font-medium">Facebook:</h4>
            <p>{result.facebookPost}</p>
          </div>
          <div className="linkedin-preview">
            <h4 className="font-medium">LinkedIn:</h4>
            <p>{result.linkedinPost}</p>
          </div>
        </div>
      )}
      <div>
        <h3 className="text-xl font-semibold mb-4">Stored Posts</h3>
        {storedPosts.map((post) => (
          <div key={post.id} className="mb-4 border rounded-lg overflow-hidden">
            <div
              className="flex justify-between items-center p-4 bg-gray-100 cursor-pointer"
              onClick={() => togglePost(post.id)}
            >
              <h4 className="font-medium">{post.url}</h4>
              {expandedPost === post.id ? <ChevronUp /> : <ChevronDown />}
            </div>
            {expandedPost === post.id && (
              <div className="p-4">
                <div className="mb-2 twitter-preview">
                  <h5 className="font-medium">Twitter:</h5>
                  <p>{post.twitterPost}</p>
                </div>
                <div className="mb-2 facebook-preview">
                  <h5 className="font-medium">Facebook:</h5>
                  <p>{post.facebookPost}</p>
                </div>
                <div className="linkedin-preview">
                  <h5 className="font-medium">LinkedIn:</h5>
                  <p>{post.linkedinPost}</p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Add CSS styles for previews
const styles = `
.twitter-preview {
  border: 1px solid #1DA1F2;
  padding: 10px;
  border-radius: 5px;
  background-color: #E8F5FD;
}

.facebook-preview {
  border: 1px solid #1877F2;
  padding: 10px;
  border-radius: 5px;
  background-color: #E7F3FF;
}

.linkedin-preview {
  border: 1px solid #0077B5;
  padding: 10px;
  border-radius: 5px;
  background-color: #EAF3F9;
}
`;

// Inject styles into the document
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default SocialMedia;